/**
 * Volet « notifications » des Réglages (specs §6.7 et §6.8, archi §6.4).
 *
 * 🔴 **C'est le seul endroit de l'application qui demande la permission de
 * notifier, et elle ne part que sur un tap.** Le §6.4 l'impose nommément, et
 * c'est la même règle qu'à l'étape 2 pour la caméra. Elle est plus stricte
 * encore ici : un refus de notification est **définitif**, aucune API ne
 * permet de reposer la question. Une demande au démarrage, refusée par
 * réflexe, coûterait la fonctionnalité pour de bon.
 *
 * ⚠️ **Deux réglages de nature différente cohabitent, et l'écran doit le dire.**
 *   - « Recevoir sur cet appareil » est un **abonnement** : il ne concerne que
 *     l'iPhone ou l'ordinateur devant lequel on se trouve.
 *   - Les trois interrupteurs sont des **réglages de compte** : les couper
 *     éteint la notification partout.
 * Confondre les deux ferait chercher longtemps pourquoi « c'est activé » et que
 * rien n'arrive.
 *
 * ⚠️ **Les heures ne sont pas celles de l'appareil**, mais celles du fuseau
 * réglé pour les notifications. Contre-pied assumé des retours de l'étape 8,
 * qui ont fait passer le front au fuseau de l'appareil : au moment où le
 * scheduler évalue « aucune pesée aujourd'hui », il n'y a pas de navigateur
 * pour lui donner l'heure.
 *
 * 🔴 **D'où le réglage de fuseau, ajouté à l'étape 11.** Il était figé sur la
 * variable d'environnement du serveur, posée à `Europe/Paris` lors de
 * l'installation du VPS. Pour un utilisateur vivant neuf heures plus à l'est,
 * le bilan de fin de journée réglé sur 21 h 30 arrivait donc à **6 h 30 du
 * matin**, et le rappel de pesée « du matin » en milieu d'après-midi. Rien
 * n'était cassé — ni le push, ni le scheduler, ni les réglages : personne
 * n'avait jamais dit au serveur où vivait son utilisateur.
 */

import { readError } from '../../api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type {
  NotificationSettingDTO,
  NotificationType,
  NotificationsResponse,
} from '../../../../shared/types';
import { ApiError } from '../../api/client';
import {
  NOTIFICATIONS_KEY,
  fetchNotifications,
  registerSubscription,
  saveNotificationSetting,
  saveTimeZone,
  sendTestNotification,
  unregisterSubscription,
} from '../../api/notifications';
import { Button, Card, ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { TOKENS } from '../../components/ui/tokens';
import { allTimeZones, deviceTimeZone, offsetLabel, timeAt } from '../../lib/timezone';
import {
  PushEnableError,
  currentSubscription,
  disablePush,
  enablePush,
  isIOS,
  isStandalone,
  permissionState,
  pushSupported,
  toSubscriptionInput,
} from '../../lib/push';

/** Libellés et explications des trois notifications (specs §6.7). */
const LABELS: Record<NotificationType, { title: string; detail: string }> = {
  weigh_in: {
    title: 'Rappel de pesée',
    detail: 'Le matin, si aucune pesée n’est enregistrée pour la journée.',
  },
  empty_log: {
    title: 'Journal vide',
    detail: 'En soirée, si aucune entrée n’a été saisie dans la journée.',
  },
  daily_summary: {
    title: 'Bilan de fin de journée',
    detail: 'À heure fixe, avec le consommé et le restant de macros.',
  },
};

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof PushEnableError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Échec inattendu.';
}

/* ==========================================================================
 * Fuseau des notifications
 * ======================================================================= */

/**
 * Le réglage le plus conséquent du volet, et celui qui ne se voit pas.
 *
 * 🔴 Il ne décale pas seulement l'heure d'envoi : la **date** sur laquelle les
 * conditions sont évaluées en dépend aussi (« aucune pesée aujourd'hui »).
 * D'où le parti pris d'affichage — montrer les **deux** horloges côte à côte
 * plutôt qu'un nom de fuseau seul. « Europe/Paris » ne dit rien à personne à
 * 6 h du matin ; « il y est 21:30, il est 06:30 ici » se comprend d'un coup
 * d'œil, et c'est exactement la phrase qui manquait pour trouver le défaut.
 *
 * ⚠️ Le raccourci « utiliser le fuseau de cet appareil » est là parce qu'il
 * couvre le cas courant en un tap. Le menu complet reste dessous : on peut
 * vouloir régler un fuseau qui n'est pas celui de l'appareil qu'on tient —
 * régler depuis l'ordinateur les notifications qu'on recevra sur le téléphone,
 * par exemple.
 */
function TimeZoneRow({
  data,
  disabled,
  onChange,
}: {
  data: NotificationsResponse;
  disabled: boolean;
  onChange: (zone: string | null) => void;
}) {
  const device = deviceTimeZone();
  const here = device === null ? null : timeAt(device);
  const gap = device === null ? null : offsetLabel(device, data.timeZone);
  const zones = allTimeZones(data.timeZone);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <span style={{ fontSize: '0.85rem', color: TOKENS.textQuiet }}>
          Fuseau des notifications
        </span>
        <select
          value={data.timeZone}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="ui-select"
        >
          {zones.map((zoneName) => (
            <option key={zoneName} value={zoneName}>
              {zoneName}
            </option>
          ))}
        </select>
      </label>

      {/*
        Les deux horloges, toujours. C'est la comparaison qui informe, pas le
        nom du fuseau — et c'est elle qui rend un mauvais réglage évident.
      */}
      <Hint>
        Il est {data.serverTime} à {data.timeZone}
        {here !== null && device !== data.timeZone ? `, et ${here} sur cet appareil` : ''}.
        {gap !== null ? ` Décalage : ${gap}.` : ''} C’est ce fuseau qui décide de l’envoi :
        au moment où le serveur vérifie s’il reste une pesée à saisir, aucun appareil n’est là
        pour lui donner l’heure.
      </Hint>

      {device !== null && device !== data.timeZone && (
        <Button
          variant="quiet"
          disabled={disabled}
          onClick={() => {
            onChange(device);
          }}
        >
          Utiliser le fuseau de cet appareil ({device})
        </Button>
      )}

      {!data.timeZoneIsExplicit && (
        <Hint>
          Aucun fuseau n’a été réglé : c’est celui de la machine qui s’applique. Il a été posé
          à l’installation du serveur et n’a aucune raison d’être le vôtre.
        </Hint>
      )}
    </div>
  );
}

/* ==========================================================================
 * Une ligne de réglage
 * ======================================================================= */

function SettingRow({
  setting,
  disabled,
  onChange,
}: {
  setting: NotificationSettingDTO;
  disabled: boolean;
  onChange: (next: { enabled: boolean; time: string }) => void;
}) {
  const { title, detail } = LABELS[setting.type];

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          // Cible tactile ≥ 44 px (specs §8.1). Elle est portée par le
          // `<label>`, jamais par le contrôle : un interrupteur WebKit et une
          // case à cocher n'ont pas la même taille propre.
          minHeight: '44px',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <span style={{ fontWeight: 500 }}>{title}</span>

        {/*
          🔴 `switch` — interrupteur natif de WebKit (Safari 17.4+), **et le
          seul retour haptique que l'application obtienne sur iPhone** : le
          système joue un tic tactile à chaque bascule réelle. Ce n'est pas un
          contournement, c'est le contrôle employé pour ce qu'il est ; le
          contournement — déclencher ce tic par script sur un interrupteur
          caché — a été fermé par Apple en iOS 26.5 (cf. `lib/haptics.ts`).

          ⚠️ Sur un moteur qui ignore l'attribut (Chromium, donc le banc), c'est
          une case à cocher ordinaire. Même sémantique, même nom accessible :
          le repli est complet, et une capture de banc montrant une case n'est
          pas un défaut de rendu.

          ⚠️ Plus de libellé « Activée / Désactivée » à côté : un interrupteur
          dit son état par sa position, et le doubler d'un mot faisait deux
          choses à lire pour une seule information. La case de repli, elle,
          reste cochée ou non.
        */}
        <input
          type="checkbox"
          switch=""
          className="ui-switch"
          role="switch"
          checked={setting.enabled}
          disabled={disabled}
          onChange={(event) => {
            onChange({ enabled: event.target.checked, time: setting.time });
          }}
        />
      </label>

      <Hint>{detail}</Hint>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', color: TOKENS.textQuiet }}>Heure</span>
          {/*
            ⚠️ Le champ ne porte plus `colorScheme: 'dark'` en ligne — il vaut
            désormais celui de `:root`, qui suit `data-theme`. C'est ce que
            l'étape 9 avait laissé à celle-ci : figé en sombre, ce champ serait
            devenu illisible dès la première bascule en thème clair.
          */}
          <input
            type="time"
            className="ui-time"
            value={setting.time}
            disabled={disabled}
            onChange={(event) => {
              // Un champ `time` vidé renvoie une chaîne vide : on n'envoie rien
              // plutôt que d'écrire une heure invalide que le serveur refusera.
              if (event.target.value === '') return;
              onChange({ enabled: setting.enabled, time: event.target.value });
            }}
          />
        </label>

        {setting.lastSentDate !== null && (
          <span style={{ fontSize: '0.8rem', color: TOKENS.textFaint }}>
            envoyée le {setting.lastSentDate}
          </span>
        )}
      </div>
    </Card>
  );
}

/* ==========================================================================
 * Volet
 * ======================================================================= */

export function NotificationsSection() {
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState(permissionState);
  const [subscribedHere, setSubscribedHere] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const notifications = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: fetchNotifications,
  });

  const write = (fresh: NotificationsResponse) => {
    queryClient.setQueryData(NOTIFICATIONS_KEY, fresh);
  };

  /*
   * Relit l'abonnement local **et le redéclare au serveur**.
   *
   * ⚠️ Ce n'est pas redondant avec l'activation. Le navigateur peut renouveler
   * un abonnement de lui-même, et le serveur peut avoir supprimé la ligne après
   * un 404/410 (§6.4) sans que le navigateur en sache rien : les deux côtés
   * peuvent donc se croire d'accord alors qu'ils ne le sont plus. Rejouer
   * l'abonnement à l'ouverture de l'écran est la seule façon de les réaccorder,
   * et c'est gratuit — la route est idempotente par `endpoint`.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const subscription = await currentSubscription();
      if (cancelled) return;

      setSubscribedHere(subscription?.endpoint ?? null);
      if (!subscription) return;

      try {
        const fresh = await registerSubscription(toSubscriptionInput(subscription));
        if (!cancelled) write(fresh);
      } catch {
        // Sans session ou hors ligne : l'écran affiche déjà l'erreur de la
        // requête principale, inutile d'en empiler une seconde.
      }
    })();

    return () => {
      cancelled = true;
    };
    // Une seule fois à l'ouverture du volet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = useMutation({
    mutationFn: async () => {
      const key = notifications.data?.vapidPublicKey;
      if (!key) {
        throw new Error(
          'Le serveur n’est pas configuré pour les notifications (clés VAPID absentes).',
        );
      }
      const input = await enablePush(key);
      const fresh = await registerSubscription(input);
      return { endpoint: input.endpoint, fresh };
    },
    onSuccess: ({ endpoint, fresh }) => {
      setPermission(permissionState());
      setSubscribedHere(endpoint);
      setNotice('Cet appareil recevra les notifications activées ci-dessous.');
      write(fresh);
    },
    onError: () => {
      setPermission(permissionState());
    },
  });

  const disable = useMutation({
    mutationFn: async () => {
      const endpoint = await disablePush();
      // Déjà désabonné côté navigateur : on nettoie quand même le serveur avec
      // l'endpoint connu, sinon la ligne y resterait jusqu'au prochain envoi.
      const target = endpoint ?? subscribedHere;
      if (target === null) return null;
      return unregisterSubscription(target);
    },
    onSuccess: (fresh) => {
      setSubscribedHere(null);
      setNotice('Cet appareil ne recevra plus de notification.');
      if (fresh) write(fresh);
    },
  });

  const update = useMutation({
    mutationFn: ({
      type,
      body,
    }: {
      type: NotificationType;
      body: { enabled: boolean; time: string };
    }) => saveNotificationSetting(type, body),
    onSuccess: write,
  });

  /**
   * Fuseau des notifications.
   *
   * ⚠️ Aucune mise à jour optimiste : changer de fuseau déplace l'heure **et**
   * la date que le serveur emploie, et la réponse rapporte les deux recalculés.
   * Les deviner côté client demanderait de refaire le calcul du serveur pour
   * l'écraser un aller-retour plus tard — le mauvais marché, et le même
   * raisonnement qu'à l'étape 8 pour les pesées.
   */
  const zone = useMutation({
    mutationFn: (value: string | null) => saveTimeZone(value),
    onSuccess: (fresh) => {
      write(fresh);
      setNotice(`Fuseau des notifications : ${fresh.timeZone}, où il est ${fresh.serverTime}.`);
    },
  });

  const test = useMutation({
    mutationFn: sendTestNotification,
    onSuccess: (result) => {
      setNotice(
        result.sent > 0
          ? `Notification d’essai envoyée à ${String(result.sent)} appareil(s).`
          : 'Aucun appareil abonné : rien n’a été envoyé.',
      );
    },
  });

  const data = notifications.data;
  const supported = pushSupported();
  const needsInstall = isIOS() && !isStandalone();
  const busy = enable.isPending || disable.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <h2 style={{ fontSize: '1rem', margin: 0 }}>Notifications</h2>

      <ReadNote error={readError(notifications)} quoi="les réglages de notification" />

      {/*
        ⚠️ Le cas le plus fréquent en pratique, et celui qu'il faut expliquer
        plutôt que constater : sur iPhone, tant que l'application n'est pas sur
        l'écran d'accueil, `Notification` n'existe pas et aucun bouton ne peut
        marcher (§6.4 ; specs §6.7, contrainte acceptée).
      */}
      {needsInstall && (
        <Hint>
          Sur iPhone, les notifications n’existent qu’une fois l’application installée sur
          l’écran d’accueil : bouton Partager, puis « Sur l’écran d’accueil ». Rouvrir
          l’application depuis cette icône, puis revenir ici.
        </Hint>
      )}

      {!supported && !needsInstall && (
        <Hint>Ce navigateur ne prend pas en charge les notifications push.</Hint>
      )}

      {data?.vapidPublicKey === null && (
        <Hint>
          Le serveur n’est pas configuré pour les notifications : les clés VAPID sont
          absentes de son environnement (§9).
        </Hint>
      )}

      {/* --- Abonnement de CET appareil ---------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {enable.isError && <ErrorNote>{describe(enable.error)}</ErrorNote>}
        {disable.isError && <ErrorNote>{describe(disable.error)}</ErrorNote>}

        {subscribedHere === null ? (
          <Button
            disabled={!supported || busy || data?.vapidPublicKey == null}
            onClick={() => {
              setNotice(null);
              enable.mutate();
            }}
          >
            {enable.isPending ? 'Activation…' : 'Recevoir les notifications sur cet appareil'}
          </Button>
        ) : (
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => {
              setNotice(null);
              disable.mutate();
            }}
          >
            {disable.isPending ? 'Désactivation…' : 'Ne plus recevoir sur cet appareil'}
          </Button>
        )}

        {permission === 'denied' && (
          <Hint>
            Les notifications ont été refusées pour ce site. Le navigateur ne reposera pas la
            question : il faut les réautoriser dans les réglages du système.
          </Hint>
        )}

        {data && (
          <Hint>
            {data.deviceCount === 0
              ? 'Aucun appareil abonné.'
              : `${String(data.deviceCount)} appareil(s) abonné(s)${
                  subscribedHere === null ? ' — pas celui-ci.' : ', celui-ci compris.'
                }`}
          </Hint>
        )}
      </div>

      {/* --- Les trois réglages, valables pour tous les appareils --------- */}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {data.settings.map((setting) => (
            <SettingRow
              key={setting.type}
              setting={setting}
              disabled={update.isPending}
              onChange={(body) => {
                setNotice(null);
                update.mutate({ type: setting.type, body });
              }}
            />
          ))}
        </div>
      )}

      {update.isError && <ErrorNote>{describe(update.error)}</ErrorNote>}

      {/*
        Fuseau des trois heures, affiché **en permanence** et non seulement
        quand il diffère de celui de l'appareil.

        Ce n'est pas la comparaison des deux horloges qui est utile, c'est le
        fait que ces trois heures-là ne se lisent pas dans le fuseau de
        l'appareil — à contre-pied de tout le reste de l'application depuis les
        retours de l'étape 8. Ne le dire que les jours de décalage laisserait
        croire, le reste du temps, que le réglage suit l'appareil.
      */}
      {data && (
        <TimeZoneRow
          data={data}
          disabled={zone.isPending}
          onChange={(value) => {
            setNotice(null);
            zone.mutate(value);
          }}
        />
      )}

      {zone.isError && <ErrorNote>{describe(zone.error)}</ErrorNote>}

      {/* --- Essai ------------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {test.isError && <ErrorNote>{describe(test.error)}</ErrorNote>}
        <Button
          variant="quiet"
          disabled={test.isPending || (data?.deviceCount ?? 0) === 0}
          onClick={() => {
            setNotice(null);
            test.mutate();
          }}
        >
          {test.isPending ? 'Envoi…' : 'Envoyer une notification d’essai'}
        </Button>
        <Hint>
          Vérifie que le push arrive vraiment sur cet appareil, sans attendre l’heure d’une
          des trois notifications.
        </Hint>
      </div>

      {notice !== null && <Hint>{notice}</Hint>}
    </div>
  );
}
